// Page-level Layout used by every authenticated screen in the
// dashboard. It provides:
//   - An optional `sidebar` slot (typically <Sidebar />) rendered as a
//     persistent left column on md+ and a drawer on mobile. The
//     Sidebar owns its own brand + nav; the Layout's top header
//     therefore stays minimal on md+ (just user identity + the
//     "Back to Ki-Wealth" action) and shows the brand wordmark on
//     mobile where the sidebar is hidden by default.
//   - A consistent top header with a "Back to Ki-Wealth" action.
//   - A `children` slot for the active feature module(s).
//
// Visual primitives (`app-shell`, `app-main`, `app-header`, …) live in
// src/theme.css so the entire SPA stays on one design language.

import type { ReactNode } from 'react';

import { useCurrentUser } from '../../features/profile';
import Button from './Button';
import ForgeUltraFooter from './forge-ultra-footer';

const KI_WEALTH_INSIGHTS_URL = 'https://www.ki-wealth.com/the-insights-lab';

interface LayoutProps {
    title?: string;
    subtitle?: string;
    /**
     * Optional left-rail slot. Pass `<Sidebar />` from `@/shared/ui`
     * to enable in-app navigation. Pages that don't want a sidebar
     * (e.g. an empty-state error page) can omit it.
     */
    sidebar?: ReactNode;
    children: ReactNode;
}

export const Layout = ({ title, subtitle, sidebar, children }: LayoutProps) => {
    const handleBackToKiWealth = () => {
        // Send the user back to the public Ki-Wealth marketing site.
        // We intentionally do NOT clear the local JWT here — the user is
        // simply navigating away, not signing out. Involuntary logouts
        // (token expiry / 401) are still handled centrally in api/client.ts.
        window.location.assign(KI_WEALTH_INSIGHTS_URL);
    };

    // Display the currently signed-in user's name to the left of the
    // "Back to Ki-Wealth" button. Prefer ``full_name`` (the WP display
    // name), fall back to ``email`` so the slot is never empty for an
    // authenticated visitor whose WP profile lacks a real name. While
    // /users/me is in flight (or if it errors) we render nothing rather
    // than a placeholder, so the header does not flicker.
    const currentUser = useCurrentUser();
    const userLabel =
        currentUser.status === 'ready'
            ? currentUser.user.full_name?.trim() || currentUser.user.email
            : null;

    return (
        <div className="app-shell">
            {sidebar}
            <div className="app-main">
                <header className="app-header">
                    <div className="app-header-inner">
                        {/* Brand only on mobile — the sidebar shows it on md+. */}
                        <div className="brand-wordmark text-xl md:hidden">
                            Forge <span className="brand-accent">Ultra</span>
                        </div>
                        {/* Spacer pushes user actions to the right on md+. */}
                        <div className="hidden md:block flex-1" />
                        <div className="flex items-center gap-3 sm:gap-4">
                            {userLabel && (
                                <span
                                    className="text-sm sm:text-base font-medium text-slate-700 dark:text-slate-300 truncate max-w-[12rem] sm:max-w-xs"
                                    title={userLabel}
                                >
                                    {userLabel}
                                </span>
                            )}
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={handleBackToKiWealth}
                            >
                                Back to Ki-Wealth
                            </Button>
                        </div>
                    </div>
                </header>

                <main className="app-main-content">
                    {(title || subtitle) && (
                        <div className="mb-8">
                            {title && <h1 className="page-title">{title}</h1>}
                            {subtitle && <p className="page-subtitle">{subtitle}</p>}
                        </div>
                    )}
                    {children}
                </main>

                {/* Rendered from Layout — not from each page — so every
                    authenticated screen ships with the legal disclaimer
                    by construction. Do NOT move this into individual
                    pages without agreeing a replacement strategy with
                    the legal team. */}
                <ForgeUltraFooter />
            </div>
        </div>
    );
};

export default Layout;
